import { Box, Typography } from "@mui/material";
import { modeloPublicacaoSistemaEnum } from "../utils/constants";
import { getNomeOrgaoPublicacaoPedido, getTenantMunicipio } from "../utils/tenantDocumentConfig";

const TITULO_MUDANCA_TITULARIDADE =
  "REQUERIMENTO DE LICENÇA/AUTORIZAÇÃO AMBIENTAL COM MUDANÇA DE TITULARIDADE";
const TITULO_PROCESSO_EM_TRAMITE =
  "REQUERIMENTO DE MUDANÇA DE TITULARIDADE COM PROCESSO EM TRÂMITE";
const TITULO_LICENCA_AUTORIZACAO_AMBIENTAL =
  "REQUERIMENTO DE LICENÇA/AUTORIZAÇÃO AMBIENTAL";
const TITULO_RENOVACAO =
  "REQUERIMENTO PARA RENOVAÇÃO DE LICENÇA/AUTORIZAÇÃO AMBIENTAL";
const TITULO_REGULARIZACAO =
  "REQUERIMENTO PARA REGULARIZAÇÃO DE LICENÇA AMBIENTAL";

const COR_FUNDO_PREVIA = "#fafafa";
const COR_FUNDO_DOCUMENTO = "#ffffff";
const COR_TEXTO_PREVIA = "#111111";
const COR_TEXTO_LEGENDA = "#555555";
const COR_BORDA_DOCUMENTO = "#111111";

function formatarDataCriacao(dataCriacao) {
  const data = dataCriacao == null ? new Date() : new Date(dataCriacao);
  if (Number.isNaN(data.getTime())) {
    return "";
  }
  const day = String(data.getDate()).padStart(2, "0");
  const month = String(data.getMonth() + 1).padStart(2, "0");
  const year = data.getFullYear();
  return `${day}/${month}/${year}`;
}

export default function PreviaModeloPublicacaoPedido({
  modelo = modeloPublicacaoSistemaEnum.REQUERIMENTO_LICENCA_AUTORIZACAO,
  nomeOuRazaoSocial,
  tipoLicencaNome,
  textoTipoLicenca,
  atividade,
  endereco,
  numeroProcessoEmTramite,
  dataCriacao,
}) {
  const mudancaTitularidade =
    Number(modelo) === modeloPublicacaoSistemaEnum.REQUERIMENTO_MUDANCA_TITULARIDADE;
  const processoEmTramite =
    Number(modelo) ===
    modeloPublicacaoSistemaEnum.REQUERIMENTO_MUDANCA_TITULARIDADE_PROCESSO_EM_TRAMITE;
  const licencaAutorizacaoAmbiental =
    Number(modelo) ===
    modeloPublicacaoSistemaEnum.REQUERIMENTO_LICENCA_AUTORIZACAO_AMBIENTAL;
  const renovacao =
    Number(modelo) ===
    modeloPublicacaoSistemaEnum.REQUERIMENTO_RENOVACAO_LICENCA_AUTORIZACAO;
  const regularizacao =
    Number(modelo) ===
    modeloPublicacaoSistemaEnum.REQUERIMENTO_REGULARIZACAO_LICENCA;
  const novoTitular = mudancaTitularidade || processoEmTramite;
  const municipio = getTenantMunicipio().municipio || "__________";
  const orgao = getNomeOrgaoPublicacaoPedido();
  const titular =
    (nomeOuRazaoSocial || "").trim() ||
    (novoTitular
      ? "(Nome do novo titular da licença/autorização)"
      : regularizacao
        ? "(Nome do titular da licença)"
        : "(Nome do titular da licença/autorização)");
  const tipoTitulo = (tipoLicencaNome || "").trim();
  const titulo = processoEmTramite
    ? TITULO_PROCESSO_EM_TRAMITE
    : mudancaTitularidade
      ? TITULO_MUDANCA_TITULARIDADE
      : licencaAutorizacaoAmbiental
        ? TITULO_LICENCA_AUTORIZACAO_AMBIENTAL
        : renovacao
          ? TITULO_RENOVACAO
          : regularizacao
            ? TITULO_REGULARIZACAO
            : tipoTitulo
              ? `REQUERIMENTO DE LICENÇA/${tipoTitulo}`.toUpperCase()
              : "REQUERIMENTO DE LICENÇA";
  const categoria =
    (textoTipoLicenca || "").trim() || (processoEmTramite ? "(2)" : "(1)");
  const tipoLicenca = (tipoLicencaNome || "").trim() || "(2)";
  const tipo = (textoTipoLicenca || "").trim() || "(1)";
  const numeroProcesso = (numeroProcessoEmTramite || "").trim() || "(1)";
  const atividadeTexto =
    (atividade || "").trim() || (novoTitular ? "(3)" : "(2)");
  const enderecoTexto =
    (endereco || "").trim() || (novoTitular ? "(4)" : "(3)");
  const dataTexto = formatarDataCriacao(dataCriacao);

  return (
    <Box
      data-testid="previa-modelo-publicacao-pedido"
      sx={{
        border: "1px solid",
        borderColor: "divider",
        bgcolor: COR_FUNDO_PREVIA,
        color: COR_TEXTO_PREVIA,
        p: 2,
        borderRadius: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{ display: "block", mb: 1, color: COR_TEXTO_LEGENDA }}
      >
        Prévia do modelo (o PDF final incluirá cabeçalho e rodapé do PDF Layout)
      </Typography>

      <Typography
        align="center"
        color="inherit"
        sx={{ fontWeight: 700, fontSize: "0.95rem", mb: 2, textTransform: "uppercase" }}
      >
        {titulo}
      </Typography>

      <Box
        sx={{
          border: `2px solid ${COR_BORDA_DOCUMENTO}`,
          p: 2,
          bgcolor: COR_FUNDO_DOCUMENTO,
          color: COR_TEXTO_PREVIA,
        }}
      >
        <Typography
          align="center"
          color="inherit"
          sx={{ fontWeight: 700, mb: 1.5, fontSize: "0.95rem" }}
        >
          {titular}
        </Typography>
        <Typography
          color="inherit"
          sx={{ fontSize: "0.875rem", mb: 1.5, lineHeight: 1.5 }}
        >
          {processoEmTramite ? (
            <>
              Torna público que requereu à {orgao} a Mudança de Titularidade do
              processo SPU nº <strong>{numeroProcesso}</strong>, referente à{" "}
              <strong>{categoria}</strong> para <strong>{atividadeTexto}</strong>,
              localizada no município de <strong>{municipio}</strong>, na{" "}
              <strong>{enderecoTexto}</strong>.
            </>
          ) : renovacao ? (
            <>
              Torna público que requereu à {orgao} a Renovação da{" "}
              <strong>{tipo}</strong> para <strong>{atividadeTexto}</strong>,
              localizada no município de <strong>{municipio}</strong>, na{" "}
              <strong>{enderecoTexto}</strong>.
            </>
          ) : regularizacao ? (
            <>
              Torna público que requereu à {orgao} a Regularização de{" "}
              <strong>{tipo}</strong> para <strong>{atividadeTexto}</strong>,
              localizada no município de <strong>{municipio}</strong>, na{" "}
              <strong>{enderecoTexto}</strong>.
            </>
          ) : mudancaTitularidade ? (
            <>
              Torna público que requereu à {orgao} a <strong>{categoria}</strong> da{" "}
              <strong>{tipoLicenca}</strong> com Mudança de Titularidade para{" "}
              <strong>{atividadeTexto}</strong>, localizada no município de{" "}
              <strong>{municipio}</strong>, na <strong>{enderecoTexto}</strong>.
            </>
          ) : (
            <>
              Torna público que requereu à {orgao} a <strong>{tipo}</strong> para{" "}
              <strong>{atividadeTexto}</strong>, localizada no município de{" "}
              <strong>{municipio}</strong>, na <strong>{enderecoTexto}</strong>.
            </>
          )}
        </Typography>
        <Typography color="inherit" sx={{ fontSize: "0.875rem", lineHeight: 1.5, mb: 1.5 }}>
          Foi determinado o cumprimento das exigências contidas nas Normas e
          Instruções de Licenciamento da {orgao}.
        </Typography>
        {dataTexto && (
          <Typography
            color="inherit"
            align="right"
            sx={{ fontSize: "0.875rem", lineHeight: 1.5 }}
          >
            Data: {dataTexto}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
